import React, { useMemo } from "react";

import Tooltip from "antd/es/tooltip";

import { Activity, Company, Person } from "@mapmycustomers/shared/types/entity";

import ActivityDate from "@app/component/activity/ActivityDate";
import ActivityOwnerAssignee from "@app/component/activity/ActivityOwnerAssignee";
import ActivityTypeIcon from "@app/component/activity/ActivityTypeIcon";
import EmailMetrics from "@app/component/EmailMetrics";
import EmailsMetrics from "@app/component/EmailsMetrics";
import getActivityEmailMetrics from "@app/util/email/getActivityEmailMetrics";
import isAutoEmailActivity from "@app/util/email/isAutoEmailActivity";

import SectionLayout from "../SectionLayout";

import AddressLine from "./AddressLine";
import styles from "./TopSection.module.scss";

interface TopSectionProps {
  activity: Activity;
  addressRecord: Company | Person | undefined;
  className?: string;
  title: string;
}

const TopSection = ({ activity, addressRecord, className, title }: TopSectionProps) => {
  // Engagement comes from the per-recipient logs when the read supplied them: a
  // merged multi-contact send anchors the singular `emailLog` on one arbitrary
  // recipient, so it under-reports the activity as a whole.
  const emailMetrics = useMemo(() => getActivityEmailMetrics(activity), [activity]);

  return (
    <SectionLayout
      className={className}
      left={
        <ActivityTypeIcon
          activityType={activity.crmActivityType}
          isAutoEmail={isAutoEmailActivity(activity)}
          size="md"
        />
      }
    >
      <Tooltip mouseEnterDelay={1} title={title}>
        <div className={styles.title}>{title}</div>
      </Tooltip>
      <AddressLine addressRecord={addressRecord} />
      <div className={styles.dateRow}>
        <ActivityDate activity={activity} />
        {emailMetrics.type === "aggregate" ? (
          // Multi-recipient email: show open/click as counts across the recipients
          // the email actually reached.
          <EmailsMetrics
            clicked={emailMetrics.counts.clickedCount}
            opened={emailMetrics.counts.openedCount}
            total={emailMetrics.counts.total}
            undelivered={emailMetrics.counts.undeliveredCount}
          />
        ) : (
          <EmailMetrics email={emailMetrics.log} />
        )}
      </div>
      <ActivityOwnerAssignee activity={activity} />
    </SectionLayout>
  );
};

export default TopSection;
